import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { RoleDto } from '@lmt/shared-types';
import { ScrollToTop } from '@lmt/unicorn-shared/app/components/common/table/helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import React, { FC, useEffect, useRef } from 'react';
import {
  localizePermission,
  localizePermissionGroup,
} from '@lmt/unicorn-shared/app/helpers/localization/translations';
import { snakeCase } from 'lodash';

import styles from '@lmt/unicorn-shared/app/components/patients/list/components/PatientsTable.module.scss';
import { TableDotMenu } from '../../../common/table/TableDotMenu';
import { getGroupsByPermissions } from '../../common';
import localStyles from './RolesWithGroupsTable.module.scss';
import clsx from 'clsx';
import { useUniSelector } from '@lmt/unicorn-shared/app/store/types';
import { getSettings } from '@lmt/unicorn-shared/app/features/settings/selectors';
import { getBGColor } from '../../../calendar/color-settings/components/color-parser';

/**
 * List of mappings which we do not display
 */

type Props = {
  noResults: boolean;
  data: RoleDto[];
  onDelete: (roleId: number) => void;
  onEdit: (roleId: number, isDefault?: boolean) => void;
};

const nameof = nameofFactory<RoleDto>();

const columnStyles = {
  [nameof('name')]: {
    minWidth: '200px',
    width: '30%',
    sortable: false,
  },
  [nameof('permissions')]: {
    minWidth: '60px',
    width: '70%',
    sortable: false,
  },
  ['actions']: {
    minWidth: '10px',
    width: '0.1%',
  },
};

export const RolesWithGroupsTable: FC<Props> = React.memo(
  function RolesTable(props) {
    const i18n = useScopedTranslation('roles.list');
    const isNightTheme = useUniSelector(getSettings).isNightTheme;

    const tableRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (tableRef.current !== null) {
        ScrollToTop(tableRef.current);
      }
    }, [props.data]);

    return (
      <TableContainer
        ref={tableRef}
        className={styles.tableContainer}
        component={Paper}
      >
        <Table stickyHeader className={styles.table}>
          <TableHead>
            <TableRow>
              {Object.keys(columnStyles).map((columnKey) => (
                <TableCell
                  key={`${columnKey}`}
                  onClick={() => columnStyles[columnKey].sortable}
                  className={styles.header}
                  style={columnStyles[columnKey]}
                >
                  <div className={styles.headerCell}>
                    {i18n.t(`columns.${columnKey}` as any)}
                  </div>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.data.map((role) => (
              <TableRow
                key={role.id}
                hover
                onClick={() =>
                  props.onEdit(role.id, role.isDefault || !role.isEditable)
                }
              >
                <TableCell>
                  <div className={styles.titleCell}>
                    {i18n.t(`default_roles.${snakeCase(role.name)}`, role.name)}
                  </div>
                </TableCell>
                <TableCell>
                  <div
                    className={clsx(
                      styles.listCell,
                      localStyles['group-container'],
                    )}
                  >
                    {role.permissions &&
                      getGroupsByPermissions(role.permissions)
                        .map((group) => {
                          return {
                            title: localizePermissionGroup(group.name),
                            color: getBGColor(group.color, isNightTheme),
                          };
                        })
                        .sort((a, b) => a.title.localeCompare(b.title))
                        .map((group) => {
                          return (
                            <div
                              key={group.title}
                              className={localStyles.group}
                              style={{ backgroundColor: group.color }}
                            >
                              {' '}
                              {group.title}
                            </div>
                          );
                        })}
                  </div>
                </TableCell>
                <TableCell
                  className={styles.optionCell}
                  onClick={(event: any) => event.stopPropagation()}
                >
                  {' '}
                  {role.isEditable && !role.isDefault ? (
                    <TableDotMenu
                      options={[
                        {
                          title: i18n.t('options.edit_button'),
                          onClick: () => props.onEdit(role.id),
                          disabled: !role.isEditable || role.isDefault,
                        },
                        {
                          title: i18n.t('options.delete_button'),
                          onClick: () => props.onDelete(role.id),
                          isDeleteItem: true,
                          isUnderLine: true,
                          disabled: !role.isEditable || role.isDefault,
                        },
                      ]}
                    />
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  },
);
